import ExcelJS from "exceljs";

// Cede el hilo al navegador cada cierto número de filas para que la pestaña no se congele
const ROWS_PER_CHUNK = 500;
const yieldToBrowser = () => new Promise((resolve) => setTimeout(resolve, 0));

export default class DataTableExcel {
  // Limpia etiquetas HTML de los strings retornados por los formatters de Vue
  static stripHtml(html) {
    if (typeof html !== "string") return html;
    return html.replace(/<[^>]*>?/gm, "");
  }

  // Formateo recursivo para objetos y arreglos anidados
  static formatValue(val, depth = 0) {
    if (val === null || val === undefined) return "";
    const indent = "  ".repeat(depth);

    if (Array.isArray(val)) {
      return val
        .map(
          (item) =>
            `${indent}- ${DataTableExcel.formatValue(item, depth + 1).trim()}`,
        )
        .join("\n");
    }

    if (typeof val === "object" && !(val instanceof Date)) {
      return Object.entries(val)
        .map(([key, value]) => {
          const formattedValue = DataTableExcel.formatValue(value, depth + 1);
          const separator =
            typeof value === "object" && value !== null ? "\n" : " ";
          return `${indent}${key}:${separator}${formattedValue}`;
        })
        .join("\n");
    }

    return String(val);
  }

  static async generateWorkbook(data, options = {}) {
    const workbook = new ExcelJS.Workbook();
    const sheetName = options.sheetName || "Datos";
    const worksheet = workbook.addWorksheet(sheetName);

    // Preparación de campos y tipos
    const validFields = options.fields
      ? options.fields.filter((f) => !f.buttons)
      : [];
    const fieldTypes = {};

    const getNestedValue = (obj, keyPath) =>
      keyPath
        .split(".")
        .reduce((acc, key) => (acc ? acc[key] : undefined), obj);

    // Extraer encabezados y tipos manuales
    const headers = validFields.map((field) => {
      if (field.type) fieldTypes[field.text] = field.type;
      return field.text;
    });

    // Inferencia de tipos protegida (muestreo de 10 filas)
    if (data.length > 0) {
      const sample = data.slice(0, 10);
      validFields.forEach((field) => {
        if (!fieldTypes[field.text] && !field.format) {
          for (let item of sample) {
            const val = getNestedValue(item, field.value);
            if (val === null || val === undefined || val === "") continue;

            if (typeof val === "number") {
              fieldTypes[field.text] = "number";
              break;
            }
            // Validación de fecha estricta para evitar falsos positivos en nombres
            if (
              typeof val === "string" &&
              val.length > 9 &&
              val.includes("-") &&
              !isNaN(Date.parse(val))
            ) {
              fieldTypes[field.text] = "date";
              break;
            }
          }
        }
      });
    }

    // Renderizado de Título
    if (options.rawText || options.title) {
      worksheet.mergeCells(1, 1, 1, headers.length || 1);
      const titleCell = worksheet.getCell("A1");
      titleCell.value = options.rawText || options.title;
      titleCell.font = { bold: true, size: 18 };
      titleCell.alignment = { horizontal: "center", vertical: "middle" };
      titleCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: options.titleBgColor || "FFADD8E6" },
      };
      worksheet.addRow([]);
    }

    // Renderizado de Encabezados
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true, size: 12 };
    headerRow.alignment = { horizontal: "center", vertical: "middle" };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: options.headerBgColor || "FFD3D3D3" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Ancho de columnas: se calcula con el encabezado y una muestra de filas.
    // (column.values arma un arreglo con todas las filas de cada columna)
    const SAMPLE_ROWS = 50;
    const maxLengths = headers.map((h) => String(h).length || 10);
    if ((options.rawText || options.title) && maxLengths.length) {
      maxLengths[0] = Math.max(
        maxLengths[0],
        String(options.rawText || options.title).length,
      );
    }

    // Preparación de Filas 
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      const rowData = validFields.map((field) => {
        // Formatos de config
        if (field.format) {
          return DataTableExcel.stripHtml(field.format(item));
        }

        const rawValue = getNestedValue(item, field.value);
        const type = fieldTypes[field.text];

        if (rawValue === null || rawValue === undefined) return "";

        switch (type) {
          case "number": {
            const num = parseFloat(rawValue);
            // Si la conversión falla (NaN), devolvemos el string original
            return isNaN(num) ? String(rawValue) : num;
          }
          case "date": {
            const date = new Date(rawValue);
            // Si la fecha es inválida, devolvemos el string original
            return isNaN(date.getTime()) ? String(rawValue) : date;
          }
          case "boolean": {
            return rawValue ? "Sí" : "No";
          }
          default: {
            return DataTableExcel.formatValue(rawValue);
          }
        }
      });

      if (i < SAMPLE_ROWS) {
        rowData.forEach((val, col) => {
          if (val) {
            const textLength = val.toString().length;
            if (textLength > maxLengths[col]) maxLengths[col] = textLength;
          }
        });
      }

      const row = worksheet.addRow(rowData);
      row.alignment = { vertical: "middle", wrapText: true };
      if ((i + 1) % ROWS_PER_CHUNK === 0) await yieldToBrowser();
    }

    // Aplicar formatos de columna (Excel nativo)
    headers.forEach((header, index) => {
      const column = worksheet.getColumn(index + 1);
      const type = fieldTypes[header];
      if (type === "number") column.numFmt = "#,##0.00";
      if (type === "date") column.numFmt = "yyyy-mm-dd";
    });

    // Autoajuste de ancho 
    maxLengths.forEach((len, index) => {
      worksheet.getColumn(index + 1).width = Math.min(len + 2, 50);
    });

    return workbook;
  }

  static async generateExcelBuffer(data, options = {}) {
    const workbook = await DataTableExcel.generateWorkbook(data, options);
    return await workbook.xlsx.writeBuffer();
  }
}
/**
 * Ejemplo de usos:
 * * |-> Ejemplo 1: Exportación básica (Auto-detección de tipos con columnas de tabla)
 * |
 * v
   exportExcel() {
     const data = [
       { id: "A123", name: "John Doe", age: 30, active: true, salary: "1500.75", joinDate: "2024-03-10" },
       { id: "B456", name: "Jane Smith", age: "25", active: false, salary: "2000", joinDate: "2024-02-15" }
     ];

     // DataTableExcel requiere definir las columnas (como las de Vuetify/Quasar)
     const config = {
       fields: [
         { text: "ID", value: "id" },
         { text: "Nombre", value: "name" },
         { text: "Edad", value: "age" },
         { text: "Activo", value: "active" },
         { text: "Salario", value: "salary" },
         { text: "Fecha", value: "joinDate" }
       ] // Al no especificar "type", el código detectará automáticamente números, booleanos y fechas.
     };

     DataTableExcel.generateExcelBuffer(data, config)
       .then(buffer => {
         const blob = new Blob([buffer], {
           type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
         });
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = "AutoDetect.xlsx";
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
       })
       .catch(error => console.error("Error exportando:", error));
   },
 * * |-> Ejemplo 2: Exportación con configuración personalizada y filtrado de botones
 * |
 * v
   exportExcelWithConfig() {
     const data = [
       { id: "A123", name: "John Doe", age: 30, active: true, salary: "1500.75", joinDate: "2024-03-10" },
       { id: "B456", name: "Jane Smith", age: "25", active: false, salary: "2000", joinDate: "2024-02-15" }
     ];

     const config = {
       title: "Empleados",
       sheetName: "Empleados",
       fields: [
         { text: "ID", value: "id", type: "string" },
         { text: "Nombre", value: "name", type: "string" },
         { text: "Edad", value: "age", type: "number" },
         { text: "Salario", value: "salary", type: "number" },
         { text: "Activo", value: "active", type: "boolean" },
         { text: "Fecha de Ingreso", value: "joinDate", type: "date" },
         { text: "Acciones", value: "actions", buttons: true } // <- Se ignorará automáticamente al exportar
       ],
       titleBgColor: "FFADD8E6", // Azul claro.
     };

     DataTableExcel.generateExcelBuffer(data, config)
       .then(buffer => {
         const blob = new Blob([buffer], {
           type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
         });
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = "Empleados.xlsx";
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
       })
       .catch(error => console.error("Error exportando:", error));
   },
 * * |-> Ejemplo 3: Exportación con formato personalizado y limpieza de HTML
 * |
 * v
   exportExcelWithFormat() {
     const data = [
       { cliente: "Carlos López", pago: { monto: 1500, pagado: true } },
       { cliente: "Ana Gómez", pago: { monto: 2500, pagado: false } }
     ];

     const config = {
       title: "Pagos de Clientes",
       sheetName: "Pagos",
       fields: [
         { text: "Cliente", value: "cliente", type: "string" },
         { text: "Monto", value: "pago.monto", type: "number" },
         { 
           text: "Estado", 
           value: "pago", 
           // El formatter recibe la fila completa (item). 
           // El código limpiará automáticamente las etiquetas <b> y <span> para Excel.
           format: (item) => item.pago.pagado 
             ? `<b>Pagado: $${item.pago.monto}</b>` 
             : "<span style='color:red'>Pendiente</span>"
         } 
       ],
       titleBgColor: "FFFF99CC", // Rosa claro.
     };

     DataTableExcel.generateExcelBuffer(data, config)
       .then(buffer => {
         const blob = new Blob([buffer], {
           type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
         });
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = "Pagos.xlsx";
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
       })
       .catch(error => console.error("Error exportando:", error));
   },
 */